import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Select } from "antd";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

export interface VisitEntry {
  date: string;
  doctor: string;
  specialty: string;
  initials: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const YEAR_PICKER_SPAN = 30;

function formatVisitDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]}, ${d.getFullYear()}`;
}

export function RecentVisitsModal({
  visits,
  initialMonth,
  initialYear,
  onClose,
}: {
  visits: VisitEntry[];
  initialMonth: number;
  initialYear: number;
  onClose: () => void;
}) {
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - YEAR_PICKER_SPAN; y--) years.push(y);
    return years;
  }, []);

  const visibleMonths = useMemo(() => {
    const start = Math.max(0, Math.min(month - 1, MONTHS.length - 4));
    return MONTHS.slice(start, start + 4).map((label, i) => ({ label, index: start + i }));
  }, [month]);

  const visitsForMonth = useMemo(
    () =>
      visits.filter((v) => {
        const d = new Date(v.date + "T00:00:00");
        return d.getFullYear() === year && d.getMonth() === month;
      }),
    [visits, year, month]
  );

  return (
    <AppModal
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      width={440}
      hideCloseButton
      className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!max-h-[90vh] [&_.ant-modal-content]:!overflow-y-auto"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold text-[#111827]">Recent Visits</h2>
        <Button
          variant="ghost"
          onClick={onClose}
          className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !p-0 !text-[#9CA3AF] hover:!bg-[#F3F4F6] hover:!text-[#111827]"
        >
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      <div className="mt-4 flex justify-end">
        <Select
          value={year}
          onChange={setYear}
          options={yearOptions.map((y) => ({ label: y, value: y }))}
          popupMatchSelectWidth={false}
          className="!w-24 [&_.ant-select-selector]:!rounded-full [&_.ant-select-selector]:!border-[#E5E7EB] [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-item]:!font-semibold [&_.ant-select-selection-item]:!text-[#111827] [&_.ant-select-selector]:hover:!bg-[#F3F4F6]"
        />
      </div>

      <div className="mt-3 flex items-center gap-1">
        <Button
          variant="outline"
          onClick={() => setMonth((m) => Math.max(0, m - 1))}
          className="!flex !h-9 !w-9 !shrink-0 !items-center !justify-center !rounded-xl !border-[#E5E7EB] !bg-white !p-0 hover:!border-[#E5E7EB] hover:!bg-[#F3F4F6]"
        >
          <Icon icon="lucide:chevron-left" className="h-4 w-4 text-[#4B5563]" />
        </Button>
        <div className="flex flex-1 justify-between">
          {visibleMonths.map((m) => (
            <Button
              key={m.index}
              variant="ghost"
              onClick={() => setMonth(m.index)}
              className={cn(
                "!h-auto !rounded-xl !px-4 !py-2 !text-sm !font-bold",
                m.index === month
                  ? "!bg-[#F36F8F] !text-white hover:!bg-[#F36F8F] hover:!text-white"
                  : "!bg-[#F3F4F6] !text-[#111827] hover:!bg-[#E5E7EB] hover:!text-[#111827]"
              )}
            >
              {m.label}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          onClick={() => setMonth((m) => Math.min(11, m + 1))}
          className="!flex !h-9 !w-9 !shrink-0 !items-center !justify-center !rounded-xl !border-[#E5E7EB] !bg-white !p-0 hover:!border-[#E5E7EB] hover:!bg-[#F3F4F6]"
        >
          <Icon icon="lucide:chevron-right" className="h-4 w-4 text-[#4B5563]" />
        </Button>
      </div>

      <div className="mt-5 space-y-4">
        {visitsForMonth.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#9CA3AF]">No visits in this month.</p>
        ) : (
          visitsForMonth.map((v, i) => (
            <div key={`${v.date}-${i}`}>
              <p className="mb-2 text-xs font-semibold text-[#9CA3AF]">{formatVisitDate(v.date)}</p>
              <div className="flex items-center gap-3 rounded-xl bg-[#F3F4F6] px-4 py-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F36F8F] text-xs font-bold text-white">
                  {v.initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#111827]">{v.doctor}</p>
                  {v.specialty && <p className="text-xs text-[#6B7280]">{v.specialty}</p>}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </AppModal>
  );
}
